<template>
    <v-dialog v-model="dialog" max-width="800px" persistent>
        <v-card>
            <v-card-title class="headline">
                Bulk Forecast Creation
            </v-card-title>

            <v-card-text>
                <!-- Selected Scopes List -->
                <v-list dense class="mb-4">
                    <v-list-item
                        v-for="scope in selectedScopesData"
                        :key="scope.id"
                        class="px-0"
                    >
                        <v-list-item-content>
                            <v-list-item-title>{{
                                scope.name
                            }}</v-list-item-title>
                            <v-list-item-subtitle>{{
                                scope.quoteName
                            }}</v-list-item-subtitle>
                            <v-list-item-subtitle class="text-caption">
                                Price:
                                {{
                                    formatCurrency(
                                        scope.quoteCurrency,
                                        scope.totalPrice
                                    )
                                }}
                                | Invoiced:
                                {{
                                    formatCurrency(
                                        scope.quoteCurrency,
                                        scope.totalInvoiced
                                    )
                                }}
                                | Current Forecast:
                                {{
                                    formatCurrency(
                                        scope.quoteCurrency,
                                        getCurrentForecastedAmount(scope)
                                    )
                                }}
                                ({{ getCurrentForecastPercentage(scope) }}%)
                            </v-list-item-subtitle>
                            <v-list-item-subtitle
                                class="text-caption text--primary"
                            >
                                Available for Forecast:
                                {{
                                    formatCurrency(
                                        scope.quoteCurrency,
                                        getAvailableForForecast(scope)
                                    )
                                }}
                                ({{ getAvailableForecastPercentage(scope) }}%)
                            </v-list-item-subtitle>
                            <v-list-item-subtitle class="text-caption">
                                New Forecast:
                                {{
                                    formatCurrency(
                                        scope.quoteCurrency,
                                        getScopeForecastAmount(scope)
                                    )
                                }}
                                ({{ getScopeForecastPercentage() }}%)
                            </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn
                                icon
                                small
                                color="error"
                                @click="removeScope(scope.id)"
                            >
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>

                <v-divider class="mb-4"></v-divider>

                <!-- Forecast Form -->
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="6">
                            <v-menu
                                v-model="menu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="forecastData.date"
                                        label="Forecast Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[
                                            v => !!v || 'Date is required',
                                        ]"
                                        required
                                    />
                                </template>
                                <v-date-picker
                                    v-model="forecastData.date"
                                    @input="menu = false"
                                    :min="min"
                                    type="month"
                                    scrollable
                                    :allowed-dates="allowedDates"
                                />
                            </v-menu>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="forecastData.percentage"
                                type="number"
                                label="Percentage"
                                prepend-icon="mdi-percent"
                                @input="updateAllScopeForecasts()"
                                :rules="[rules.number, maxPercentage()]"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn text @click="closeDialog">
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    :disabled="!valid || loading"
                    :loading="loading"
                    @click="createBulkForecast"
                >
                    Create Forecast
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import moment from 'moment'
import _ from 'lodash'

export default {
    name: 'BulkForecastDialog',
    props: {
        dialog: {
            type: Boolean,
            default: false,
        },
        selectedScopes: {
            type: Array,
            default: () => [],
        },
        scopes: {
            type: Array,
            default: () => [],
        },
        project: {
            type: Object,
            default: () => ({}),
        },
        forecastOrigin: {
            type: Object,
            default: () => ({}),
        },
        dates: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            valid: false,
            loading: false,
            menu: false,
            min: new Date().toISOString(),
            rules: {
                required: v => !!v || 'Required',
                number: v => v >= 0 || 'Not valid',
            },
            forecastData: {
                date: '',
                percentage: '',
            },
            maxAmountValue: 0,
            isCurrentMonth: false,
            forecast: {},
        }
    },
    computed: {
        selectedScopesData() {
            if (!this.scopes || !Array.isArray(this.scopes)) {
                return []
            }
            if (!this.selectedScopes || !Array.isArray(this.selectedScopes)) {
                return []
            }
            return this.scopes.filter(scope =>
                this.selectedScopes.includes(scope.id)
            )
        },
    },
    watch: {
        dialog(newVal) {
            if (newVal) {
                this.resetForm()
                this.initializeScopeForecasts()
            }
        },
        'forecastData.date'() {
            this.updateAllScopeForecasts()
        },
    },
    async mounted() {
        try {
            this.loading = true
            if (this.forecastOrigin && typeof this.forecastOrigin === 'object') {
                this.forecast = _.cloneDeep(this.forecastOrigin)
            } else {
                this.forecast = { scopes: {} }
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        resetForm() {
            this.forecastData = {
                date: '',
                percentage: '',
            }
            this.valid = false
            if (this.$refs.form) {
                this.$refs.form.resetValidation()
            }
        },
        initializeScopeForecasts() {
            if (!this.selectedScopesData || !Array.isArray(this.selectedScopesData)) {
                return
            }
            this.selectedScopesData.forEach(scope => {
                if (scope && typeof scope === 'object') {
                    scope.forecastAmount = 0
                }
            })
        },
        allowedDates(val) {
            const currentDate = moment().format('YYYY-MM')
            return moment(val).isSameOrAfter(currentDate, 'month')
        },
        calculateForecastedAmount(scope) {
            const currentDate = moment().format('YYYY-MM')

            // Check if the selected date is the current month
            this.isCurrentMonth =
                this.forecastData.date && this.forecastData.date == currentDate

            let total = 0

            if (!this.forecast || !this.forecast.scopes || typeof this.forecast.scopes !== 'object') {
                return total
            }

            const forecastKey = Object.keys(this.forecast.scopes).find(
                key => this.forecast.scopes[key] && this.forecast.scopes[key].id == scope.id
            )

            if (forecastKey) {
                const forecastDates = this.forecast.scopes[forecastKey].dates

                if (forecastDates) {
                    Object.values(forecastDates).forEach(date => {
                        if (date.forecast) {
                            const forecast = date.forecast
                            if (
                                moment(forecast.date).isSameOrAfter(currentDate)
                            ) {
                                total += Number(forecast.amount) || 0
                            }
                        }
                        if (date.amount) {
                            if (moment(date.date).isSameOrAfter(currentDate)) {
                                total += Number(date.amount) || 0
                            }
                        }
                    })
                }

                const currentEntry = Object.values(forecastDates).find(e => {
                    let entryDate = e.date || e.forecast?.date
                    if (!entryDate) return false
                    return moment(entryDate).isSame(
                        this.forecastData.date,
                        'day'
                    )
                })

                if (currentEntry) {
                    total -= Number(
                        currentEntry.amount ??
                            currentEntry.forecast?.amount ??
                            0
                    )
                }
            }
            return total
        },
        maxPercentage() {
            // Calculate max percentage based on available balance for each scope
            let maxPercentageValue = 100
            let mostRestrictiveScope = null

            this.selectedScopesData.forEach(scope => {
                const availableForForecast = this.getAvailableForForecast(scope)
                const scopeMaxPercentage =
                    (availableForForecast / scope.totalPrice) * 100

                if (scopeMaxPercentage < maxPercentageValue) {
                    maxPercentageValue = scopeMaxPercentage
                    mostRestrictiveScope = scope
                }
            })

            if (this.forecastData.percentage > maxPercentageValue) {
                return `The percentage cannot be greater than ${maxPercentageValue.toFixed(
                    2
                )}%. Limited by scope: ${mostRestrictiveScope?.name ||
                    'Unknown'}`
            } else {
                return true
            }
        },
        formatCurrency(currency, value) {
            if (currency) {
                const formattedValue = new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: currency,
                    maximumFractionDigits: 0,
                }).format(+value || 0)
                return formattedValue
            }
            return '0'
        },
        async createBulkForecast() {
            if (!this.$refs.form.validate()) return

            try {
                this.loading = true

                const forecastEntries = this.selectedScopesData.map(scope => {
                    const scopeAmount =
                        (Number(this.forecastData.percentage) / 100) *
                        scope.totalPrice

                    return {
                        scopeId: scope.id,
                        date: this.forecastData.date,
                        amount: parseFloat(scopeAmount.toFixed(2)),
                        percentage: parseFloat(this.forecastData.percentage),
                    }
                })

                const updatedForecast = await API.createBulkForecast({
                    projectId: this.project.id,
                    entries: forecastEntries,
                })

                this.$emit('onBulkForecastCreated', updatedForecast)
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDialog() {
            this.$emit('closeBulkForecastDialog')
        },
        getScopeForecastAmount(scope) {
            return (
                (Number(this.forecastData.percentage || 0) / 100) *
                scope.totalPrice
            ).toFixed(2)
        },
        getScopeForecastPercentage() {
            return this.forecastData.percentage || 0
        },
        updateAllScopeForecasts() {
            this.selectedScopesData.forEach(scope => {
                scope.forecastAmount = (
                    (Number(this.forecastData.percentage || 0) / 100) *
                    scope.totalPrice
                ).toFixed(2)
            })
        },
        getCurrentForecastedAmount(scope) {
            return this.calculateForecastedAmount(scope)
        },
        getCurrentForecastPercentage(scope) {
            const currentForecasted = this.getCurrentForecastedAmount(scope)
            return ((currentForecasted / scope.totalPrice) * 100).toFixed(2)
        },
        getAvailableForForecast(scope) {
            const currentForecasted = this.getCurrentForecastedAmount(scope)
            return scope.totalPrice - scope.totalInvoiced - currentForecasted
        },
        getAvailableForecastPercentage(scope) {
            const available = this.getAvailableForForecast(scope)
            return ((available / scope.totalPrice) * 100).toFixed(2)
        },
        removeScope(scopeId) {
            this.$emit('removeScopeFromSelection', scopeId)
        },
    },
}
</script>

<style scoped>
.v-list-item {
    border-bottom: 1px solid #eee;
}

.v-list-item:last-child {
    border-bottom: none;
}
</style>
